import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, SlidersHorizontal, X, Eye, ChevronDown, ChevronUp } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Pagination from '../components/Pagination';
import CommonLoader from '../components/CommonLoader';
import ErrorPage from '../components/ErrorPage';
import AppointmentDetailsModal from '../components/modals/AppointmentDetailsModal';
import { FilterPopover, FilterPanel, FilterSection, FilterOptionList, FilterSearchableList } from '../components/common/FilterPanel';
import MasterLabel from '../components/common/MasterLabel';
import { filterAppointments } from '../redux/appointmentSlice';
import { getDoctorById, getDoctorNameList, clearSelectedDoctor } from '../redux/doctorSlice';
import { getPatienttsNameList } from '../redux/patientSlice';
import { getDoctorMissingSetupItems } from '../utils/doctorSetupStatus';
import errorimg from '../assets/img/colorkit (1).svg';

/* -------------------------------------------------
   Debounce Hook (exact copy from DoctorDashboard)
   ------------------------------------------------- */
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
};

export default function AppointmentList() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation('appointmentlist');

    const filterRef = useRef(null);
    const filterButtonRef = useRef(null);
    const dropdownRef = useRef(null);
    const isInitialCall = useRef(true);
    const lastSelectedDoctor = useRef(null);

    const { payments: appointments, paymentsMetadata, isLoading, error } = useSelector(s => s.appointment);
    const { patientNameList } = useSelector(s => s.patient);
    const { selectedDoctor, doctorNameList, loading: loadingDoctor, loadingList } = useSelector(s => s.doctor);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchTermError, setSearchTermError] = useState('');
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedDoctorState, setSelectedDoctorState] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');               // dropdown search for doctors
    const [searchQueryError, setSearchQueryError] = useState('');
    const [dateErrors, setDateErrors] = useState({ startDate: '', endDate: '' });
    const emptyFilters = { startDate: '', endDate: '', status: [], selectedPatient: [], specialtyId: [] };
    const [filters, setFilters] = useState(emptyFilters);
    const [tempFilters, setTempFilters] = useState(emptyFilters);
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [patientOptions, setPatientOptions] = useState([]);
    const [specialtyOptions, setSpecialtyOptions] = useState([]);
    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 1024);

    const userRole = localStorage.getItem('Role');
    const Id = localStorage.getItem('ID');

    /* -------------------------------------------------
       Maximum length constants
       ------------------------------------------------- */
    const MAX_SEARCH_LENGTH = 50;

    /* -------------------------------------------------
       Validate search input
       ------------------------------------------------- */
    const validateSearchInput = (value) => {
        // Remove any potentially dangerous characters
        const sanitized = value.replace(/[<>]/g, '');
        
        // Check length
        if (sanitized.length > MAX_SEARCH_LENGTH) {
            return t('appointmentlist.search_exceed_limit', { limit: MAX_SEARCH_LENGTH });
        }
        
        // Check for only whitespace
        if (sanitized.trim().length === 0 && sanitized.length > 0) {
            return t('appointmentlist.search_only_spaces');
        }
        
        return '';
    };

    /* -------------------------------------------------
       Validate dates
       ------------------------------------------------- */
    const validateDates = (startDate, endDate) => {
        const errors = { startDate: '', endDate: '' };
        
        if (startDate && endDate) {
            const start = new Date(startDate);
            const end = new Date(endDate);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            if (start > end) {
                errors.startDate = t('appointmentlist.start_date_after_end');
            }
            
            if (end > today) {
                errors.endDate = t('appointmentlist.end_date_in_future');
            }
        }
        
        if (endDate) {
            const end = new Date(endDate);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            if (end > today) {
                errors.endDate = t('appointmentlist.end_date_in_future');
            }
        }
        
        return errors;
    };

    /* -------------------------------------------------
       Debounce for dropdown search (only if no error)
       ------------------------------------------------- */
    const debouncedSearch = useDebounce(searchQuery, 400);

    /* -------------------------------------------------
       Resize handling
       ------------------------------------------------- */
    useEffect(() => {
        const handleResize = () => setIsSmallScreen(window.innerWidth < 1024);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    /* -------------------------------------------------
       Initial data load
       ------------------------------------------------- */
    useEffect(() => {
        if (isInitialCall.current) {
            if (userRole === 'admin') {
                dispatch(clearSelectedDoctor());
                dispatch(getDoctorNameList());               // initial list (no search)
            } else if (userRole === 'doctor') {
                dispatch(getDoctorById(Id));
                dispatch(getPatienttsNameList(Id));
            }
            isInitialCall.current = false;
        }
    }, [dispatch, userRole, Id]);

    /* -------------------------------------------------
       Search doctors when dropdown is open (only if no error)
       ------------------------------------------------- */
    useEffect(() => {
        if (userRole === 'admin' && isOpen && !searchQueryError) {
            dispatch(getDoctorNameList(debouncedSearch));
        }
    }, [debouncedSearch, dispatch, userRole, isOpen, searchQueryError]);

    /* -------------------------------------------------
       Close dropdown on outside click
       ------------------------------------------------- */
    useEffect(() => {
        const handleClickOutside = e => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false);
                setSearchQuery('');
                setSearchQueryError('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    /* -------------------------------------------------
       Patient & specialty options
       ------------------------------------------------- */
    useEffect(() => {
        if (patientNameList.length) {
            setPatientOptions(patientNameList.map(p => ({ id: p.patientId, name: p.name || '' })));
        } else {
            setPatientOptions([]);
        }
    }, [patientNameList]);

    useEffect(() => {
        if (appointments?.length && selectedDoctor?.appointmentSpeciality) {
            const map = new Map();
            selectedDoctor.appointmentSpeciality.forEach(s => {
                if (s.specialtyId && s.name) map.set(s.specialtyId, s.name);
            });
            setSpecialtyOptions(Array.from(map, ([id, name]) => ({ id, name })));
        }
    }, [appointments, selectedDoctor]);

    /* -------------------------------------------------
       Reset page when search term changes
       ------------------------------------------------- */
    useEffect(() => {
        if (searchTerm) setCurrentPage(1);
    }, [searchTerm]);

    /* -------------------------------------------------
       Fetch filtered appointments
       ------------------------------------------------- */
    useEffect(() => {
        if (userRole === 'admin' && !selectedDoctorState) return;
        const doctorId = userRole === 'admin' ? selectedDoctorState?.doctorId : Id;
        if (doctorId) {
            const params = {
                ...(filters.startDate && { startDate: filters.startDate }),
                ...(filters.endDate && { endDate: filters.endDate }),
                ...(filters.status.length > 0 && { appointmentStatus: filters.status.join(',') }),
                ...(filters.selectedPatient.length > 0 && { patientId: filters.selectedPatient.map((p) => p.id).join(',') }),
                ...(filters.specialtyId.length > 0 && { SpecialtyId: filters.specialtyId.join(',') }),
                doctorId,
                page: currentPage,
                limit: itemsPerPage,
            };
            dispatch(filterAppointments(params));
        }
    }, [dispatch, filters, userRole, Id, selectedDoctorState, currentPage, itemsPerPage]);

    /* -------------------------------------------------
       Sync temp filters when filter panel opens
       ------------------------------------------------- */
    useEffect(() => {
        if (isFilterOpen) {
            setTempFilters({ ...filters });
            setDateErrors({ startDate: '', endDate: '' });
        }
    }, [isFilterOpen]);

    /* -------------------------------------------------
       Click-outside for filter panel
       ------------------------------------------------- */
    useEffect(() => {
        const handleClickOutside = e => {
            if (
                filterRef.current && !filterRef.current.contains(e.target) &&
                filterButtonRef.current && !filterButtonRef.current.contains(e.target)
            ) {
                // Reset tempFilters to current filters when closing without applying
                setTempFilters({ ...filters });
                setIsFilterOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [filters]);

    /* -------------------------------------------------
       Local search (appointmentId) + pagination
       ------------------------------------------------- */
    const filteredAppointments = React.useMemo(() => {
        if (!appointments?.length) return [];
        if (!searchTerm.trim()) return appointments;
        return appointments.filter(a =>
            a.appointmentId?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [appointments, searchTerm]);

    const currentAppointments = React.useMemo(() => {
        if (searchTerm.trim()) {
            const start = (currentPage - 1) * itemsPerPage;
            const end = start + itemsPerPage;
            return filteredAppointments.slice(start, end);
        }
        return filteredAppointments;
    }, [filteredAppointments, searchTerm, currentPage, itemsPerPage]);

    const totalItems = searchTerm.trim() ? filteredAppointments.length : (paymentsMetadata?.total || 0);

    /* -------------------------------------------------
       Handlers
       ------------------------------------------------- */
    const handlePageChange = (page, newItemsPerPage) => {
        setCurrentPage(page);
        if (newItemsPerPage && newItemsPerPage !== itemsPerPage) {
            setItemsPerPage(newItemsPerPage);
            setCurrentPage(1);
        }
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;

        // Sanitize input - remove dangerous characters
        const sanitized = value.replace(/[<>]/g, '');
        const finalValue = sanitized.length > MAX_SEARCH_LENGTH
            ? sanitized.substring(0, MAX_SEARCH_LENGTH)
            : sanitized;

        // Always update searchTerm so the input stays in sync
        setSearchTerm(finalValue);

        // Validate input
        const error = validateSearchInput(finalValue);
        setSearchTermError(error);
    };

    const handleSearchQueryChange = (e) => {
        const value = e.target.value;

        // Sanitize input - remove dangerous characters
        const sanitized = value.replace(/[<>]/g, '');
        const finalValue = sanitized.length > MAX_SEARCH_LENGTH
            ? sanitized.substring(0, MAX_SEARCH_LENGTH)
            : sanitized;

        // Always update searchQuery so the input stays in sync
        setSearchQuery(finalValue);

        // Validate input
        const error = validateSearchInput(finalValue);
        setSearchQueryError(error);
    };

    const handleApplyFilters = () => {
        // Validate dates before applying
        const errors = validateDates(tempFilters.startDate, tempFilters.endDate);
        if (errors.startDate || errors.endDate) {
            setDateErrors(errors);
            return;
        }

        setFilters({ ...tempFilters });
        setCurrentPage(1);
        setIsFilterOpen(false);
        setDateErrors({ startDate: '', endDate: '' });
    };

    const clearFilters = () => {
        setTempFilters({ ...emptyFilters });
        setFilters({ ...emptyFilters });
        setSearchTerm('');
        setSearchTermError('');
        setDateErrors({ startDate: '', endDate: '' });
        setCurrentPage(1);
        setIsFilterOpen(false);
    };

    const handleReset = () => {
        setSelectedDoctorState(null);
        setIsOpen(false);
        setSearchQuery('');
        setSearchQueryError('');
        setCurrentPage(1);
        dispatch(clearSelectedDoctor());
    };

    const handleSelectDoctor = doctor => {
        setSelectedDoctorState(doctor);
        setIsOpen(false);
        setSearchQuery('');
        setSearchQueryError('');
        setCurrentPage(1);
        if (doctor && lastSelectedDoctor.current !== doctor.doctorId) {
            lastSelectedDoctor.current = doctor.doctorId;
            dispatch(getDoctorById(doctor.doctorId));
            dispatch(getPatienttsNameList(doctor.doctorId));
        }
    };

    const handleViewDetails = appt => {
        setSelectedAppointment(appt);
        setIsModalOpen(true);
    };

    const formatDateTime = (localTime) => {
    if (!localTime) return { date: '', time: '' };
    const dateObj = new Date(localTime);
    const date = dateObj.toLocaleDateString('en-CA'); // Format as YYYY-MM-DD
    const time = dateObj.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    return time;
};

    const formatDate = d => (!d ? '-' : new Date(d).toLocaleDateString());
    const formatTime = d => (!d ? '-' : new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));

    const getStatusBadgeClass = status => {
        switch (status?.toLowerCase()) {
            case 'completed': return 'bg-gradient-to-br from-green-500/10 to-green-600/10 text-green-600 border-green-500/20';
            case 'booked': return 'bg-gradient-to-br from-blue-500/10 to-blue-600/10 text-blue-600 border-blue-500/20';
            case 'cancelled': return 'bg-gradient-to-br from-red-500/10 to-red-600/10 text-red-600 border-red-500/20';
            case 'noshow': return 'bg-gradient-to-br from-yellow-500/10 to-yellow-600/10 text-yellow-600 border-yellow-500/20';
            case 'rescheduling': return 'bg-gradient-to-br from-gray-500/10 to-gray-600/10 text-gray-600 border-gray-500/20';
            default: return 'bg-gradient-to-br from-gray-500/10 to-gray-600/10 text-gray-600 border-gray-500/20';
        }
    };

    const translateStatus = s => t(`appointmentlist.status_values.${s}`, s);

    /* -------------------------------------------------
       Render
       ------------------------------------------------- */
    // Only blank the page when the initial fetch fails (no data yet).
    // Per-action failures shouldn't hijack the whole page.
    const hasNoAppointments = !appointments || appointments.length === 0;
    if (error && hasNoAppointments && !isLoading) {
        return <ErrorPage error={error} pageName={t('appointmentlist.heading', 'Appointment List')} />;
    }

    if ((userRole === 'admin' && loadingDoctor) || (userRole === 'doctor' && isLoading && !appointments)) {
        return (
            <div data-testid="appointment-list-loading-container" className="bg-white rounded-2xl p-4 shadow-md flex flex-col items-center justify-center" style={{ height: 'calc(100vh - 96px)' }}>
                <CommonLoader />
                <span data-testid="appointment-list-loading-text" className="text-xl text-[#4ABDBD]">{t('appointmentlist.loading')}</span>
            </div>
        );
    }

    // Profile completion validation for doctors
    if (userRole === 'doctor') {
        // Get missing setup items using utility function
        const missingItemKeys = getDoctorMissingSetupItems(userRole, selectedDoctor);
        const missingItems = missingItemKeys.map(key => t(key));
        
        // If any required items are missing, show restriction page
        if (missingItems.length > 0) {
            return (
                <div data-testid="appointment-list-profile-validation-container" className="h-full bg-white rounded-2xl p-4 sm:p-6 md:p-8 lg:p-10 flex items-center justify-center">
                    <div className="text-center max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl">
                        <img data-testid="appointment-list-profile-validation-image" src={errorimg} alt={t('profileValidation.altText')} className="mx-auto w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 lg:w-56 lg:h-56 xl:w-64 xl:h-64 object-contain" />
                        <h2 data-testid="appointment-list-profile-validation-title" className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#68c0c0] mb-3 sm:mb-4">{t('profileValidation.title')}</h2>
                        <p data-testid="appointment-list-profile-validation-message" className="text-sm sm:text-base md:text-lg text-gray-800 mb-2 sm:mb-3 md:mb-4">
                            {t('profileValidation.message')}
                        </p>
                        <div data-testid="appointment-list-profile-validation-missing-section" className="text-left bg-red-50 border border-red-200 rounded-lg p-4 mb-4 sm:mb-6 md:mb-8">
                            <p data-testid="appointment-list-profile-validation-missing-label" className="text-xs sm:text-sm md:text-base text-gray-700 font-semibold mb-2">{t('profileValidation.missingItems')}:</p>
                            <ul data-testid="appointment-list-profile-validation-missing-list" className="list-disc list-inside space-y-1 text-xs sm:text-sm md:text-base text-gray-600">
                                {missingItems.map((item, index) => (
                                    <li data-testid={`appointment-list-profile-validation-missing-item-${index}`} key={index}>{item}</li>
                                ))}
                            </ul>
                        </div>
                        <button data-testid="appointment-list-profile-validation-go-to-profile-button" onClick={() => navigate('/doctorProfile')} className="px-3 py-1.5 sm:px-4 sm:py-2 md:px-5 md:py-2.5 bg-gradient-to-br from-[#4ABDBD] to-[#3A9B9B] text-white font-semibold rounded-lg shadow-sm text-xs sm:text-sm md:text-base transition-all hover:from-[#3aa8a8] hover:to-[#2a7b7b] focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] focus:ring-opacity-50 cursor-pointer">
                            {t('profileValidation.goToProfile')}
                        </button>
                    </div>
                </div>
            );
        }
    }

    return (
        <div data-testid="appointment-list-container" className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
            {/* ==================== HEADER ==================== */}
            <div data-testid="appointment-list-header" className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <h1 data-testid="appointment-list-heading" className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                    {t('appointmentlist.heading')}
                </h1>

                {/* ---- ADMIN DOCTOR DROPDOWN + FILTER BUTTON ---- */}
                {userRole === 'admin' ? (
                    <div data-testid="appointment-list-admin-controls" className="flex items-center gap-3">
                        <div data-testid="appointment-list-doctor-dropdown" ref={dropdownRef} className={`relative ${isSmallScreen ? 'w-full' : 'w-64'}`}>
                            <button
                                data-testid="appointment-list-doctor-dropdown-button"
                                onClick={() => !loadingList && setIsOpen(!isOpen)}
                                disabled={loadingList}
                                className={`w-full px-4 py-2 border rounded-xl flex justify-between items-center font-medium transition
                ${loadingList ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'text-white bg-gradient-to-br from-[#4ABDBD] to-[#3A9B9B] cursor-pointer'}
                ${isSmallScreen ? 'text-sm' : ''}`}
                            >
                                <span data-testid="appointment-list-doctor-dropdown-label" className="truncate w-[85%] text-left">
                                    {selectedDoctorState
                                        ? selectedDoctorState.name
                                        : t('appointmentlist.select_doctor')}
                                </span>
                                <span className="ml-2 flex items-center">
                                    {loadingList ? (
                                        <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                    ) : isOpen ? (
                                        <ChevronUp />
                                    ) : (
                                        <ChevronDown />
                                    )}
                                </span>
                            </button>

                            {isOpen && (
                                <div data-testid="appointment-list-doctor-dropdown-menu" className={`absolute left-0 mt-2 w-full z-50 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden ${isSmallScreen ? 'text-sm' : ''}`}>
                                    {/* Search */}
                                    <div className="p-2 border-b border-gray-200">
                                        <div className="relative">
                                            <Search data-testid="appointment-list-doctor-search-icon" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                            <input
                                                data-testid="appointment-list-doctor-search-input"
                                                type="text"
                                                value={searchQuery}
                                                onChange={handleSearchQueryChange}
                                                maxLength={50}
                                                placeholder={t('appointmentlist.search_doctor')}
                                                className={`w-full pl-10 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] ${
                                                    searchQueryError
                                                        ? 'border-red-300 bg-red-50'
                                                        : 'border-gray-300'
                                                }`}
                                                autoFocus
                                            />
                                            {searchQueryError && (
                                                <p data-testid="appointment-list-doctor-search-error" className="text-red-600 text-xs mt-1 px-2">{searchQueryError}</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Loading */}
                                    {loadingList && (
                                        <div className="p-4 flex justify-center">
                                            <span className="h-5 w-5 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin"></span>
                                        </div>
                                    )}

                                    {/* List */}
                                    {!loadingList && (
                                        <ul data-testid="appointment-list-doctor-options-list" className="max-h-60 overflow-y-auto custom-scrollbar">
                                            {/* Reset (kept commented) */}
                                            {/* <li ...>{t('appointmentlist.select_doctor')}</li> */}

                                            {doctorNameList.length === 0 ? (
                                                <li data-testid="appointment-list-doctor-no-options" className="px-4 py-3 text-gray-500 text-center">
                                                    {searchQuery ? t('appointmentlist.no_doctors_found') : t('appointmentlist.no_doctors_available')}
                                                </li>
                                            ) : (
                                                doctorNameList.map((doc, idx) => (
                                                    <li
                                                        data-testid={`appointment-list-doctor-option-${doc.doctorId}`}
                                                        key={doc.doctorId}
                                                        onClick={() => doc.available && handleSelectDoctor(doc)}
                                                        className={`px-4 py-2.5 transition flex justify-between items-center
                            ${idx !== doctorNameList.length - 1 ? 'border-b border-gray-100' : ''}
                            ${doc.available ? 'cursor-pointer hover:bg-[#4ABDBD] hover:text-white' : 'cursor-not-allowed opacity-50 bg-gray-50'}`}
                                                    >
                                                        <span data-testid={`appointment-list-doctor-option-name-${doc.doctorId}`}>{doc.name}</span>
                                                        {!doc.available && <span data-testid={`appointment-list-doctor-option-unavailable-${doc.doctorId}`} className="text-xs text-red-500">({t('appointmentlist.unavailable')})</span>}
                                                    </li>
                                                ))
                                            )}
                                        </ul>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Filter Button */}
                        <div data-testid="appointment-list-filter-section" className="relative">
                        {selectedDoctorState &&(
                              <button
                                data-testid="appointment-list-filter-button"
                                ref={filterButtonRef}
                                onClick={() => setIsFilterOpen(!isFilterOpen)}
                                className="group flex items-center justify-center gap-2 px-3 py-2.5 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer"
                            >
                                <SlidersHorizontal data-testid="appointment-list-filter-icon" className="w-5 h-5" />
                                {/* {t('appointmentlist.filter')} */}
                            </button>)}

                            {isFilterOpen && (
                                <FilterPopover ref={filterRef}>
                                    <FilterPanel
                                        onClear={clearFilters}
                                        onApply={handleApplyFilters}
                                        clearLabel={t('appointmentlist.clear')}
                                        applyLabel={t('appointmentlist.apply')}
                                    >
                                        <FilterSection title={t('appointmentlist.by_date')} isActive={!!tempFilters.startDate || !!tempFilters.endDate}>
                                            <div className="flex flex-col gap-2">
                                                <div>
                                                    <label data-testid="appointment-list-admin-start-date-label" className="block text-xs font-semibold text-gray-500 mb-1">{t('appointmentlist.from', 'From')}</label>
                                                    <input
                                                        data-testid="appointment-list-admin-start-date-input"
                                                        type="date"
                                                        value={tempFilters.startDate}
                                                        onChange={e => {
                                                            setTempFilters({ ...tempFilters, startDate: e.target.value });
                                                            if (dateErrors.startDate) {
                                                                setDateErrors(validateDates(e.target.value, tempFilters.endDate));
                                                            }
                                                        }}
                                                        className={`w-full p-2.5 rounded-lg border text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all hover:border-[#4ABDBD] ${
                                                            dateErrors.startDate ? 'border-red-300 bg-red-50' : 'border-gray-300'
                                                        }`}
                                                    />
                                                    {dateErrors.startDate && <p data-testid="appointment-list-admin-start-date-error" className="text-red-600 text-xs mt-1">{dateErrors.startDate}</p>}
                                                </div>
                                                <div>
                                                    <label data-testid="appointment-list-admin-end-date-label" className="block text-xs font-semibold text-gray-500 mb-1">{t('appointmentlist.to', 'To')}</label>
                                                    <input
                                                        data-testid="appointment-list-admin-end-date-input"
                                                        type="date"
                                                        value={tempFilters.endDate}
                                                        onChange={e => {
                                                            setTempFilters({ ...tempFilters, endDate: e.target.value });
                                                            if (dateErrors.endDate) {
                                                                setDateErrors(validateDates(tempFilters.startDate, e.target.value));
                                                            }
                                                        }}
                                                        min={tempFilters.startDate || undefined}
                                                        max={new Date().toISOString().split('T')[0]}
                                                        className={`w-full p-2.5 rounded-lg border text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all hover:border-[#4ABDBD] ${
                                                            dateErrors.endDate ? 'border-red-300 bg-red-50' : 'border-gray-300'
                                                        }`}
                                                    />
                                                    {dateErrors.endDate && <p data-testid="appointment-list-admin-end-date-error" className="text-red-600 text-xs mt-1">{dateErrors.endDate}</p>}
                                                </div>
                                            </div>
                                        </FilterSection>

                                        {((userRole === 'doctor') || (userRole === 'admin' && selectedDoctorState)) && (
                                            <FilterSection title={t('appointmentlist.by_patient')} isActive={tempFilters.selectedPatient.length > 0}>
                                                <FilterSearchableList
                                                    multiple
                                                    items={patientOptions}
                                                    value={tempFilters.selectedPatient}
                                                    onChange={(val) => setTempFilters({ ...tempFilters, selectedPatient: val })}
                                                    labelKey="name"
                                                    valueKey="id"
                                                    searchPlaceholder={t('appointmentlist.search_patient')}
                                                    noResultsText={t('appointmentlist.no_patients_found')}
                                                    allLabel={t('appointmentlist.all_patients')}
                                                />
                                            </FilterSection>
                                        )}

                                        <FilterSection title={t('appointmentlist.by_status')} isActive={tempFilters.status.length > 0}>
                                            <FilterOptionList
                                                multiple
                                                value={tempFilters.status}
                                                onChange={(val) => setTempFilters({ ...tempFilters, status: val })}
                                                options={[
                                                    { value: 'Booked', label: t('appointmentlist.status_booked') },
                                                    { value: 'Completed', label: t('appointmentlist.status_completed') },
                                                    { value: 'Cancelled', label: t('appointmentlist.status_cancelled') },
                                                    { value: 'NoShow', label: t('appointmentlist.status_missedout') },
                                                    { value: 'Rescheduling', label: t('appointmentlist.status_Rescheduling') },
                                                ]}
                                            />
                                        </FilterSection>

                                        <FilterSection title={t('appointmentlist.by_specialty')} isActive={tempFilters.specialtyId.length > 0}>
                                            <FilterOptionList
                                                multiple
                                                value={tempFilters.specialtyId}
                                                onChange={(val) => setTempFilters({ ...tempFilters, specialtyId: val })}
                                                options={specialtyOptions.map(o => ({ value: o.id, label: o.name }))}
                                            />
                                        </FilterSection>
                                    </FilterPanel>
                                </FilterPopover>
                            )}
                        </div>
                    </div>
                ) : (
                    /* ---- DOCTOR ROLE - FILTER BUTTON ONLY ---- */
                    <div data-testid="appointment-list-doctor-filter-section" className="relative">
                        <button
                            data-testid="appointment-list-doctor-filter-button"
                            ref={filterButtonRef}
                            onClick={() => setIsFilterOpen(!isFilterOpen)}
                            className="group flex items-center justify-center gap-2 px-3 py-2.5 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer"
                        >
                            <SlidersHorizontal data-testid="appointment-list-doctor-filter-icon" className="w-5 h-5" />
                        </button>

                        {isFilterOpen && (
                            <FilterPopover ref={filterRef}>
                                <FilterPanel
                                    onClear={clearFilters}
                                    onApply={handleApplyFilters}
                                    clearLabel={t('appointmentlist.clear')}
                                    applyLabel={t('appointmentlist.apply')}
                                >
                                    <FilterSection title={t('appointmentlist.by_date')} isActive={!!tempFilters.startDate || !!tempFilters.endDate}>
                                        <div className="flex flex-col gap-2">
                                            <div>
                                                <label data-testid="appointment-list-doctor-start-date-label" className="block text-xs font-semibold text-gray-500 mb-1">{t('appointmentlist.from', 'From')}</label>
                                                <input
                                                    data-testid="appointment-list-doctor-start-date-input"
                                                    type="date"
                                                    value={tempFilters.startDate}
                                                    onChange={e => {
                                                        setTempFilters({ ...tempFilters, startDate: e.target.value });
                                                        if (dateErrors.startDate) {
                                                            setDateErrors(validateDates(e.target.value, tempFilters.endDate));
                                                        }
                                                    }}
                                                    className={`w-full p-2.5 rounded-lg border text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all hover:border-[#4ABDBD] ${
                                                        dateErrors.startDate ? 'border-red-300 bg-red-50' : 'border-gray-300'
                                                    }`}
                                                />
                                                {dateErrors.startDate && <p data-testid="appointment-list-doctor-start-date-error" className="text-red-600 text-xs mt-1">{dateErrors.startDate}</p>}
                                            </div>
                                            <div>
                                                <label data-testid="appointment-list-doctor-end-date-label" className="block text-xs font-semibold text-gray-500 mb-1">{t('appointmentlist.to', 'To')}</label>
                                                <input
                                                    data-testid="appointment-list-doctor-end-date-input"
                                                    type="date"
                                                    value={tempFilters.endDate}
                                                    onChange={e => {
                                                        setTempFilters({ ...tempFilters, endDate: e.target.value });
                                                        if (dateErrors.endDate) {
                                                            setDateErrors(validateDates(tempFilters.startDate, e.target.value));
                                                        }
                                                    }}
                                                    min={tempFilters.startDate || undefined}
                                                    max={new Date().toISOString().split('T')[0]}
                                                    className={`w-full p-2.5 rounded-lg border text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all hover:border-[#4ABDBD] ${
                                                        dateErrors.endDate ? 'border-red-300 bg-red-50' : 'border-gray-300'
                                                    }`}
                                                />
                                                {dateErrors.endDate && <p data-testid="appointment-list-doctor-end-date-error" className="text-red-600 text-xs mt-1">{dateErrors.endDate}</p>}
                                            </div>
                                        </div>
                                    </FilterSection>

                                    <FilterSection title={t('appointmentlist.by_patient')} isActive={!!tempFilters.selectedPatient}>
                                        <FilterSearchableList
                                            items={patientOptions}
                                            value={tempFilters.selectedPatient}
                                            onChange={(val) => setTempFilters({ ...tempFilters, selectedPatient: val })}
                                            labelKey="name"
                                            valueKey="id"
                                            searchPlaceholder={t('appointmentlist.search_patient')}
                                            noResultsText={t('appointmentlist.no_patients_found')}
                                            allLabel={t('appointmentlist.all_patients')}
                                        />
                                    </FilterSection>

                                    <FilterSection title={t('appointmentlist.by_status')} isActive={tempFilters.status.length > 0}>
                                        <FilterOptionList
                                            multiple
                                            value={tempFilters.status}
                                            onChange={(val) => setTempFilters({ ...tempFilters, status: val })}
                                            options={[
                                                { value: 'Booked', label: t('appointmentlist.status_booked') },
                                                { value: 'Completed', label: t('appointmentlist.status_completed') },
                                                { value: 'Cancelled', label: t('appointmentlist.status_cancelled') },
                                                { value: 'NoShow', label: t('appointmentlist.status_missedout') },
                                                { value: 'Rescheduling', label: t('appointmentlist.status_Rescheduling') },
                                            ]}
                                        />
                                    </FilterSection>

                                    <FilterSection title={t('appointmentlist.by_specialty')} isActive={tempFilters.specialtyId.length > 0}>
                                        <FilterOptionList
                                            multiple
                                            value={tempFilters.specialtyId}
                                            onChange={(val) => setTempFilters({ ...tempFilters, specialtyId: val })}
                                            options={specialtyOptions.map(o => ({ value: o.id, label: o.name }))}
                                        />
                                    </FilterSection>
                                </FilterPanel>
                            </FilterPopover>
                        )}
                    </div>
                )}
            </div>

            {/* ==================== CONTENT WHEN DOCTOR SELECTED ==================== */}
            {((userRole === 'admin' && selectedDoctorState) || userRole === 'doctor') ? (
                <>

                    {/* ---- Applied Filters Display ---- */}
                    {(filters.startDate || filters.endDate || filters.status.length > 0 || filters.selectedPatient.length > 0 || filters.specialtyId.length > 0) && (
                        <div data-testid="appointment-list-applied-filters-section" className="mb-6 bg-gradient-to-r from-blue-50/80 to-cyan-50/80 rounded-xl p-4 border border-gray-200 shadow-sm">
                            <div className="flex flex-wrap items-center gap-2">
                                <span data-testid="appointment-list-applied-filters-label" className="text-sm font-semibold text-gray-700 mr-2">
                                    {t('appointmentlist.applied_filters', 'Applied Filters')}:
                                </span>

                                {filters.startDate && (
                                    <div data-testid="appointment-list-applied-start-date" className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm">
                                        <span className="text-xs text-gray-600">
                                            <span className="font-semibold">{t('appointmentlist.start_date', 'Start Date')}:</span> {new Date(filters.startDate).toLocaleDateString()}
                                        </span>
                                        <button
                                            data-testid="appointment-list-applied-start-date-clear-button"
                                            onClick={() => setFilters({ ...filters, startDate: '' })}
                                            className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                                        >
                                            <X className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                )}

                                {filters.endDate && (
                                    <div data-testid="appointment-list-applied-end-date" className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm">
                                        <span className="text-xs text-gray-600">
                                            <span className="font-semibold">{t('appointmentlist.end_date', 'End Date')}:</span> {new Date(filters.endDate).toLocaleDateString()}
                                        </span>
                                        <button
                                            data-testid="appointment-list-applied-end-date-clear-button"
                                            onClick={() => setFilters({ ...filters, endDate: '' })}
                                            className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                                        >
                                            <X className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                )}

                                {filters.status.map((s) => (
                                    <div key={`status-${s}`} className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm">
                                        <span className="text-xs text-gray-600">
                                            <span className="font-semibold">{t('appointmentlist.status', 'Status')}:</span> {translateStatus(s)}
                                        </span>
                                        <button
                                            onClick={() => setFilters({ ...filters, status: filters.status.filter((x) => x !== s) })}
                                            className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                                            data-testid={`appointment-list-applied-status-remove-button-${s}`}
                                        >
                                            <X className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                ))}

                                {filters.selectedPatient.map((p) => (
                                    <div key={`patient-${p.id}`} className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm">
                                        <span className="text-xs text-gray-600">
                                            <span className="font-semibold">{t('appointmentlist.patient', 'Patient')}:</span> {p.name || '-'}
                                        </span>
                                        <button
                                            onClick={() => setFilters({ ...filters, selectedPatient: filters.selectedPatient.filter((x) => x.id !== p.id) })}
                                            className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                                            data-testid={`appointment-list-applied-patient-remove-button-${p.id}`}
                                        >
                                            <X className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                ))}

                                {filters.specialtyId.map((sid) => (
                                    <div key={`specialty-${sid}`} className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm">
                                        <span className="text-xs text-gray-600">
                                            <span className="font-semibold">{t('appointmentlist.specialty', 'Specialty')}:</span> {specialtyOptions.find(s => s.id === sid)?.name || '-'}
                                        </span>
                                        <button
                                            onClick={() => setFilters({ ...filters, specialtyId: filters.specialtyId.filter((x) => x !== sid) })}
                                            className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                                            data-testid={`appointment-list-applied-specialty-remove-button-${sid}`}
                                        >
                                            <X className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                ))}

                                <button
                                    data-testid="appointment-list-clear-all-filters-button"
                                    onClick={clearFilters}
                                    className="ml-auto inline-flex items-center gap-1.5 bg-gradient-to-r from-red-500 to-red-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-sm hover:shadow-md cursor-pointer"
                                >
                                    <X className="h-3.5 w-3.5" />
                                    {t('appointmentlist.clear_all', 'Clear All')}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ---- Table / Cards ---- */}
                    {isLoading ? (
                        <div data-testid="appointment-list-table-loading" className="flex flex-col items-center justify-center py-4 pt-12">
                            <CommonLoader />
                            <span data-testid="appointment-list-table-loading-text" className="text-xl text-[#4ABDBD]">{t('appointmentlist.loading')}</span>
                        </div>
                    ) : (
                        <>
                            {/* Desktop */}
                            <div data-testid="appointment-list-desktop-table-container" className="hidden sm:block overflow-x-auto rounded-md border border-gray-200">
                                <table data-testid="appointment-list-desktop-table" className="w-full border-collapse rounded-md overflow-hidden">
                                    <thead>
                                        <tr className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                                            <th data-testid="appointment-list-header-serial-no" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('appointmentlist.serial_no')}</th>
                                            <th data-testid="appointment-list-header-patient-name" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('appointmentlist.patient_name')}</th>
                                            <th data-testid="appointment-list-header-specialty" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('appointmentlist.specialty')}</th>
                                            <th data-testid="appointment-list-header-date" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('appointmentlist.date')}</th>
                                            <th data-testid="appointment-list-header-time" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('appointmentlist.time')}</th>
                                            <th data-testid="appointment-list-header-status" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('appointmentlist.status')}</th>
                                            <th data-testid="appointment-list-header-action" className="p-3 text-center text-xs font-bold uppercase tracking-wider">{t('appointmentlist.action')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentAppointments.length ? (
                                            currentAppointments.map((appt, i) => (
                                                <tr data-testid={`appointment-list-row-${i}`} key={appt._id || i} className="border-b border-gray-100/50 hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-cyan-50/80 transition-all duration-300 group">
                                                    <td data-testid={`appointment-list-row-serial-${i}`} className="p-3 text-xs text-gray-600 group-hover:text-gray-700">{((currentPage - 1) * itemsPerPage) + i + 1}</td>
                                                    <td data-testid={`appointment-list-row-patient-name-${i}`} className="p-3 text-xs text-gray-600 group-hover:text-gray-700">{`${appt.patientName || ''} ${appt.patientSurname || ''}`}</td>
                                                    <td data-testid={`appointment-list-row-specialty-${i}`} className="p-3 text-xs text-gray-600 group-hover:text-gray-700">
                                                        <MasterLabel type="masterSpecialty" id={appt.SpecialtyId} field="name" fallback={appt.specialtyName || '-'} />
                                                    </td>
                                                    <td data-testid={`appointment-list-row-date-${i}`} className="p-3 text-xs text-gray-600 group-hover:text-gray-700">{formatDate(appt.scheduledDate)}</td>
                                                    <td data-testid={`appointment-list-row-time-${i}`} className="p-3 text-xs text-gray-600 group-hover:text-gray-700">{formatDateTime(appt.localTime)}</td>
                                                    <td className="p-3 text-xs">
                                                        <span data-testid={`appointment-list-row-status-${i}`} className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold border ${getStatusBadgeClass(appt.status)}`}>
                                                            {translateStatus(appt.status)}
                                                        </span>
                                                    </td>
                                                    <td className="p-3 text-xs flex justify-center">
                                                        <button
                                                            data-testid={`appointment-list-row-view-button-${i}`}
                                                            onClick={() => handleViewDetails(appt)}
                                                            className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                                        >
                                                            <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                            <Eye className="h-4 w-4 relative z-10" />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr data-testid="appointment-list-empty-row">
                                                <td data-testid="appointment-list-empty-message" colSpan="7" className="p-3 text-center text-gray-500">
                                                    {t('appointmentlist.no_appointments_found')}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile */}
                            <div data-testid="appointment-list-mobile-cards-container" className="block sm:hidden space-y-4">
                                {currentAppointments.length ? (
                                    currentAppointments.map((appt, i) => (
                                        <div data-testid={`appointment-list-card-${i}`} key={appt._id || i} className="p-4 bg-gradient-to-r bg-gray-50 rounded-xl shadow-md hover:shadow-lg transition-all duration-300">
                                            <div className="flex justify-between items-center mb-2">
                                                <span data-testid={`appointment-list-card-serial-${i}`} className="text-xs font-bold text-[#4ABDBD]">
                                                    {t('appointmentlist.serial_no')}: {((currentPage - 1) * itemsPerPage) + i + 1}
                                                </span>
                                                <button
                                                    data-testid={`appointment-list-card-view-button-${i}`}
                                                    onClick={() => handleViewDetails(appt)}
                                                    className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                                >
                                                    <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                    <Eye className="h-4 w-4 relative z-10" />
                                                </button>
                                            </div>
                                            <div className="space-y-2 text-xs text-gray-600">
                                                <div data-testid={`appointment-list-card-patient-name-${i}`}><span className="font-bold">{t('appointmentlist.patient_name')}:</span> {`${appt.patientName || ''} ${appt.patientSurname || ''}`}</div>
                                                <div data-testid={`appointment-list-card-specialty-${i}`}><span className="font-bold">{t('appointmentlist.specialty')}:</span> <MasterLabel type="masterSpecialty" id={appt.SpecialtyId} field="name" fallback={appt.specialtyName || '-'} /></div>
                                                <div data-testid={`appointment-list-card-date-${i}`}><span className="font-bold">{t('appointmentlist.date')}:</span> {formatDate(appt.scheduledDate)}</div>
                                                <div data-testid={`appointment-list-card-time-${i}`}><span className="font-bold">{t('appointmentlist.time')}:</span> {appt.timeSlot || '-'}</div>
                                                <div>
                                                    <span className="font-bold">{t('appointmentlist.status')}:</span>
                                                    <span data-testid={`appointment-list-card-status-${i}`} className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold border ml-1 ${getStatusBadgeClass(appt.status)}`}>
                                                        {translateStatus(appt.status)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div data-testid="appointment-list-mobile-empty-message" className="p-3 text-center text-gray-500">{t('appointmentlist.no_appointments_found')}</div>
                                )}
                            </div>
                        </>
                    )}

                    {/* ---- Pagination ---- */}
                    {!isLoading && totalItems > 0 && (
                        <Pagination
                            totalItems={totalItems}
                            currentPage={currentPage}
                            itemsPerPage={itemsPerPage}
                            onPageChange={handlePageChange}
                            custombutton={totalItems === 0 ? 'disable' : ''}
                        />
                    )}
                </>
            ) : (
                /* ==================== ADMIN – NO DOCTOR SELECTED ==================== */
                <div data-testid="appointment-list-no-doctor-selected-container" className="flex items-center justify-center rounded-lg p-8" style={{ height: 'calc(100vh - 300px)' }}>
                    <div className="text-center max-w-md">
                        <div className="mb-4">
                            <svg data-testid="appointment-list-no-doctor-selected-icon" className="mx-auto h-24 w-24 text-[#4ABDBD]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                        </div>
                        <h3 data-testid="appointment-list-no-doctor-selected-title" className="text-xl font-semibold text-gray-700 mb-2">{t('appointmentlist.no_doctor_selected_title')}</h3>
                        <p data-testid="appointment-list-no-doctor-selected-message" className="text-gray-500">{t('appointmentlist.select_doctor_to_view_list')}</p>
                    </div>
                </div>
            )}

            {/* ==================== MODAL ==================== */}
            <AppointmentDetailsModal
                isOpen={isModalOpen}
                onClose={() => { setIsModalOpen(false); setSelectedAppointment(null); }}
                appointment={selectedAppointment}
            />
        </div>
    );
}